import { useEffect, useRef, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { adminApi } from '../../api';
import type { AdminCredentialDto } from '../../api';
import { VerificationStatus } from '../../api/generated/boticaAPI.schemas';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { DataGrid, type DataGridItem } from '../../components/ui/DataGrid';
import { Skeleton } from '../../components/ui/Skeleton';
import { useToast } from '../../components/ui/Toast';

/* ─── Status helpers ─────────────────────────────────────────────────────── */

const STATUS_LABEL: Record<VerificationStatus, string> = {
  [VerificationStatus.PendingDocuments]: 'Unverified',
  [VerificationStatus.PendingReview]: 'Pending',
  [VerificationStatus.Verified]: 'Approved',
  [VerificationStatus.Rejected]: 'Rejected',
};

const STATUS_BADGE: Record<VerificationStatus, 'neutral' | 'pending' | 'approved' | 'rejected'> = {
  [VerificationStatus.PendingDocuments]: 'neutral',
  [VerificationStatus.PendingReview]: 'pending',
  [VerificationStatus.Verified]: 'approved',
  [VerificationStatus.Rejected]: 'rejected',
};

const TAB_FOR_STATUS: Partial<Record<VerificationStatus, string>> = {
  [VerificationStatus.Verified]: 'approved',
  [VerificationStatus.Rejected]: 'rejected',
};

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/* ─── Document section ───────────────────────────────────────────────────── */

function DocumentSection({ credentialId }: { credentialId: string }) {
  const [revealed, setRevealed] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [fetching, setFetching] = useState(false);
  const [imgError, setImgError] = useState(false);
  const prevBlobUrl = useRef<string | null>(null);

  // Revoke object URLs to avoid memory leaks
  useEffect(() => {
    return () => {
      if (prevBlobUrl.current) URL.revokeObjectURL(prevBlobUrl.current);
    };
  }, []);

  const fetchDocument = async () => {
    setFetching(true);
    setImgError(false);
    try {
      const blob = await adminApi.getCredentialDocument(credentialId);
      if (prevBlobUrl.current) URL.revokeObjectURL(prevBlobUrl.current);
      const url = URL.createObjectURL(blob);
      prevBlobUrl.current = url;
      setBlobUrl(url);
      setRevealed(true);
    } finally {
      setFetching(false);
    }
  };

  if (!revealed) {
    return (
      <div
        style={{
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-lg)',
          overflow: 'hidden',
          background: 'var(--surface-base)',
        }}
      >
        <div
          style={{
            height: 200,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            background: 'var(--warm-gray-50)',
            filter: 'blur(0)',
          }}
        >
          <svg
            width="32"
            height="32"
            viewBox="0 0 32 32"
            fill="none"
            aria-hidden
            style={{ color: 'var(--text-placeholder)' }}
          >
            <rect x="4" y="4" width="24" height="24" rx="3" stroke="currentColor" strokeWidth="1.5" />
            <path d="M10 20l4-5 3 4 2-3 3 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="11" cy="12" r="2" stroke="currentColor" strokeWidth="1.5" />
          </svg>
          <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)' }}>
            Credential document
          </p>
          <Button
            variant="secondary"
            size="sm"
            loading={fetching}
            onClick={fetchDocument}
          >
            View document
          </Button>
        </div>
        <p
          style={{
            margin: 0,
            padding: '8px 16px',
            fontSize: 'var(--text-xs)',
            color: 'var(--text-tertiary)',
            borderTop: '1px solid var(--border-subtle)',
          }}
        >
          Viewing the document logs an audit event.
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        border: '1px solid var(--border-default)',
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
        background: 'var(--surface-base)',
      }}
    >
      {imgError ? (
        <div
          style={{
            height: 200,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            background: 'var(--warm-gray-50)',
          }}
        >
          <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
            Link expired.
          </p>
          <Button variant="secondary" size="sm" loading={fetching} onClick={fetchDocument}>
            Reload document
          </Button>
        </div>
      ) : (
        <img
          src={blobUrl ?? undefined}
          alt="Credential document"
          onError={() => setImgError(true)}
          style={{
            display: 'block',
            width: '100%',
            maxHeight: 480,
            objectFit: 'contain',
            background: 'var(--warm-gray-50)',
          }}
        />
      )}
      <div
        style={{
          padding: '8px 16px',
          borderTop: '1px solid var(--border-subtle)',
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Button variant="ghost" size="sm" loading={fetching} onClick={fetchDocument}>
          Reload document
        </Button>
      </div>
    </div>
  );
}

/* ─── Decision panel ─────────────────────────────────────────────────────── */

type DecisionPanelProps = {
  credentialId: string;
  onComplete: (updated: AdminCredentialDto) => void;
};

function DecisionPanel({ credentialId, onComplete }: DecisionPanelProps) {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<'approve' | 'reject' | null>(null);

  const submit = async (action: 'approve' | 'reject') => {
    if (action === 'reject') {
      if (!reason.trim()) {
        setReasonError('A rejection reason is required.');
        return;
      }
      if (reason.trim().length < 10) {
        setReasonError('Reason must be at least 10 characters.');
        return;
      }
    }
    setReasonError(null);
    setSubmitting(action);
    try {
      const updated = await adminApi.reviewCredential(credentialId, {
        status: action === 'approve' ? VerificationStatus.Verified : VerificationStatus.Rejected,
        notes: reason.trim() || null,
      });
      toast(
        action === 'approve' ? 'Credential approved.' : 'Credential rejected.',
        action === 'approve' ? 'success' : 'neutral',
      );
      onComplete(updated);
    } catch {
      toast('Failed to process action. Try again.', 'error');
    } finally {
      setSubmitting(null);
    }
  };

  const busy = submitting !== null;

  return (
    <div
      style={{
        border: '1px solid var(--border-default)',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--surface-base)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '12px 16px',
          borderBottom: '1px solid var(--border-subtle)',
          background: 'var(--warm-gray-50)',
        }}
      >
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-xs)',
            fontWeight: 600,
            color: 'var(--text-tertiary)',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          }}
        >
          Decision
        </p>
      </div>

      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <label
            style={{
              display: 'block',
              fontSize: 'var(--text-xs)',
              fontWeight: 600,
              color: 'var(--text-tertiary)',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              marginBottom: 6,
            }}
          >
            Notes
            <span style={{ color: 'var(--badge-rejected-text)', marginLeft: 4 }}>*</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (reasonError) setReasonError(null);
            }}
            disabled={busy}
            placeholder="Required if rejecting. Optional if approving."
            rows={3}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              resize: 'vertical',
              padding: '8px 12px',
              fontSize: 'var(--text-sm)',
              fontFamily: 'var(--font-body)',
              color: 'var(--text-primary)',
              background: 'var(--surface-base)',
              border: `1px solid ${reasonError ? 'var(--badge-rejected-text)' : 'var(--border-default)'}`,
              borderRadius: 'var(--radius-md)',
              outline: 'none',
              lineHeight: 'var(--leading-normal)',
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = reasonError
                ? 'var(--badge-rejected-text)'
                : 'var(--border-focus)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = reasonError
                ? 'var(--badge-rejected-text)'
                : 'var(--border-default)';
            }}
          />
          {reasonError && (
            <p
              style={{
                margin: '4px 0 0',
                fontSize: 'var(--text-xs)',
                color: 'var(--badge-rejected-text)',
              }}
            >
              {reasonError}
            </p>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button
            variant="danger"
            size="md"
            loading={submitting === 'reject'}
            disabled={busy}
            onClick={() => submit('reject')}
          >
            Reject
          </Button>
          <Button
            variant="primary"
            size="md"
            loading={submitting === 'approve'}
            disabled={busy}
            onClick={() => submit('approve')}
          >
            Approve
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ─── CredentialDetailPage ───────────────────────────────────────────────── */

type LocationState = { credential?: AdminCredentialDto };

export function CredentialDetailPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const stateCredential = (location.state as LocationState | null)?.credential;

  const [credential, setCredential] = useState<AdminCredentialDto | null>(
    stateCredential ?? null,
  );
  const [loading, setLoading] = useState(!stateCredential);
  const [error, setError] = useState<string | null>(null);

  const fetchCredential = () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    adminApi
      .getCredentialById(id)
      .then((found) => setCredential(found))
      .catch(() => setError('Failed to load credential.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!stateCredential) fetchCredential();
  }, [id]);

  const handleDecisionComplete = (updated: AdminCredentialDto) => {
    setCredential(updated);
    const tab = (updated.verificationStatus
      ? TAB_FOR_STATUS[updated.verificationStatus]
      : undefined) ?? 'all';
    navigate(`/credentials?tab=${tab}`, { replace: true });
  };

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ maxWidth: 760 }}>
        <div style={{ marginBottom: 24 }}>
          <Skeleton height={28} width="40%" style={{ marginBottom: 8 }} />
          <Skeleton height={16} width="25%" />
        </div>
        <Skeleton height={180} style={{ borderRadius: 'var(--radius-lg)', marginBottom: 16 }} />
        <Skeleton height={200} style={{ borderRadius: 'var(--radius-lg)' }} />
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────
  if (error || !credential) {
    return (
      <div style={{ maxWidth: 760 }}>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--badge-rejected-text)', marginBottom: 12 }}>
          {error ?? 'Credential not found.'}
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="secondary" size="sm" onClick={fetchCredential}>
            Retry
          </Button>
          <Button variant="ghost" size="sm" onClick={() => navigate('/credentials')}>
            Back
          </Button>
        </div>
      </div>
    );
  }

  const v = credential.verificationStatus ?? VerificationStatus.PendingDocuments;
  const isPendingReview = v === VerificationStatus.PendingReview;
  const isReviewed = v === VerificationStatus.Verified || v === VerificationStatus.Rejected;

  const dataItems: DataGridItem[] = [
    { label: 'MPPS No.', value: credential.mppsNumber },
    { label: 'COLFAR No.', value: credential.colfarNumber },
    { label: 'IMPREFAR No.', value: credential.imprefarNumber },
    { label: 'University', value: credential.university },
    { label: 'Graduation year', value: credential.graduationYear },
    { label: 'State (Venezuela)', value: credential.state },
    { label: 'Submitted', value: formatDate(credential.submittedAt) },
    ...(isReviewed
      ? [
          { label: 'Reviewed', value: formatDate(credential.reviewedAt) },
          {
            label: 'Reviewed by',
            value: credential.reviewedBy
              ? `${credential.reviewedBy.name} (${credential.reviewedBy.email})`
              : '—',
          },
        ]
      : []),
    ...(v === VerificationStatus.Rejected && credential.rejectionReason
      ? [{ label: 'Rejection reason', value: credential.rejectionReason, span: true as const }]
      : []),
  ];

  return (
    <div style={{ maxWidth: 760 }}>
      {/* ── Back ────────────────────────────────────────────────────────── */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate(-1)}
        style={{ marginBottom: 20 }}
      >
        ← Back
      </Button>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: 24,
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h2
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--text-xl)',
              fontWeight: 600,
              color: 'var(--text-primary)',
              margin: '0 0 4px',
              letterSpacing: '-0.02em',
            }}
          >
            {credential.userDisplayName ?? 'No name'}
          </h2>
          <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
            {credential.userEmail ?? '—'}
          </p>
        </div>
        <Badge variant={STATUS_BADGE[v]} style={{ flexShrink: 0, marginTop: 4 }}>
          {STATUS_LABEL[v]}
        </Badge>
      </div>

      {/* ── Data grid ───────────────────────────────────────────────────── */}
      <DataGrid items={dataItems} columns={2} style={{ marginBottom: 16 }} />

      {/* ── Document ────────────────────────────────────────────────────── */}
      {id && (
        <div style={{ marginBottom: 16 }}>
          <p
            style={{
              margin: '0 0 8px',
              fontSize: 'var(--text-xs)',
              fontWeight: 600,
              color: 'var(--text-tertiary)',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}
          >
            Attached document
          </p>
          <DocumentSection credentialId={id} />
        </div>
      )}

      {/* ── Decision panel ──────────────────────────────────────────────── */}
      {isPendingReview && id && (
        <DecisionPanel credentialId={id} onComplete={handleDecisionComplete} />
      )}
    </div>
  );
}
